import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/api';
import { cn, getRoleLabel } from '../lib/utils';
import {
  LayoutDashboard,
  Building2,
  Users,
  ShieldCheck,
  Receipt,
  FileText,
  Award,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Store,
  UserCircle,
  ShoppingCart,
  AlertTriangle,
  Search,
  Ban,
  MessageSquare,
  FileWarning,
  Smartphone,
  Headset,
  ClipboardCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navigationByRole = {
  SUPER_ADMIN: [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Tenants', href: '/admin/tenants', icon: Building2 },
    { name: 'Customers', href: '/admin/customers', icon: UserCircle },
    { name: 'Warranty Plans', href: '/admin/warranty-plans', icon: ShieldCheck },
    { name: 'Invoices', href: '/admin/invoices', icon: Receipt },
    { name: 'Claims', href: '/admin/claims', icon: FileWarning },
    { name: 'Inquiries', href: '/admin/inquiries', icon: MessageSquare },
    { name: 'Phone Brands', href: '/admin/phone-brands', icon: Smartphone },
    { name: 'Prohibited Devices', href: '/admin/prohibited-devices', icon: Ban },
    { name: 'Warranty Lookup', href: '/admin/warranty-lookup', icon: Search },
    { name: 'Compliance', href: '/admin/compliance', icon: AlertTriangle },
    { name: 'Reports', href: '/admin/reports', icon: BarChart3 },
    { name: 'Support Team', href: '/admin/support', icon: Headset },
    { name: 'Approvals', href: '/admin/approvals', icon: ClipboardCheck },
  ],
  SHOP_OWNER: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Branches', href: '/dashboard/branches', icon: Store },
    { name: 'Staff', href: '/dashboard/staff', icon: Users },
    { name: 'New Sale', href: '/dashboard/new-sale', icon: ShoppingCart },
    { name: 'Sales', href: '/dashboard/sales', icon: FileText },
    { name: 'Claims', href: '/dashboard/claims', icon: FileWarning },
    { name: 'Invoices', href: '/dashboard/invoices', icon: Receipt },
    { name: 'Warranty Lookup', href: '/dashboard/warranty-lookup', icon: Search },
    { name: 'Incentives', href: '/dashboard/incentives', icon: Award },
    { name: 'Compliance', href: '/dashboard/compliance', icon: AlertTriangle },
    { name: 'Reports', href: '/dashboard/reports', icon: BarChart3 },
  ],
  STORE_MANAGER: [
    { name: 'Dashboard', href: '/manager', icon: LayoutDashboard },
    { name: 'New Sale', href: '/manager/new-sale', icon: ShoppingCart },
    { name: 'Bulk Entry', href: '/manager/bulk', icon: FileText },
    { name: 'Sales', href: '/manager/sales', icon: FileText },
    { name: 'Staff Performance', href: '/manager/staff', icon: Users },
    { name: 'My Performance', href: '/manager/performance', icon: Award },
    { name: 'My Earnings', href: '/manager/incentives', icon: Award },
    { name: 'Reports', href: '/manager/reports', icon: BarChart3 },
  ],
  SALES_STAFF: [
    { name: 'New Sale', href: '/sales', icon: ShoppingCart },
    { name: 'Bulk Entry', href: '/sales/bulk', icon: FileText },
    { name: 'My Sales', href: '/sales/history', icon: FileText },
    { name: 'My Earnings', href: '/sales/earnings', icon: Award },
    { name: 'My Performance', href: '/sales/performance', icon: Award },
  ],
  SUPPORT_AGENT: [
    { name: 'Chat Dashboard', href: '/support', icon: MessageSquare },
  ],
};

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = navigationByRole[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="h-16 shrink-0 flex items-center justify-between px-4 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#005DE8] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Mobikoo
            </span>
          </Link>
          <button
            className="lg:hidden p-1 rounded hover:bg-slate-100"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/admin' && item.href !== '/dashboard' && item.href !== '/manager' && item.href !== '/sales' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-50 text-[#005DE8]'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={cn('h-5 w-5', isActive ? 'text-[#005DE8]' : 'text-slate-400')} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="shrink-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
            <div className="w-9 h-9 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{getRoleLabel(user?.role)}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5 text-slate-600" />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                <UserCircle className="h-5 w-5 text-slate-500" />
                <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user?.name}</span>
                  <span className="text-xs font-normal text-slate-500">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {(user?.role === 'SHOP_OWNER' || user?.role === 'SUPER_ADMIN') && (
                <DropdownMenuItem onClick={() => navigate(user?.role === 'SHOP_OWNER' ? '/dashboard/profile' : '/admin/tenants')} data-testid="profile-button">
                  <Settings className="h-4 w-4 mr-2" />
                  {user?.role === 'SHOP_OWNER' ? 'Shop Profile' : 'Settings'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
